/**
 * Access Limit Service - Frontend Integration with Backend Access Limits
 * Handles verification-based access limitations for gigs and applications
 */

import api from './api';
import { verificationService } from './verificationService';
import { VerificationStatus } from '@/types/verification';

export interface AccessLimitStatus {
    hasAccess: boolean;
    remaining?: number;
    limit?: number;
    isUnlimited: boolean;
    requiresUpgrade: boolean;
    subscriptionTier?: string;
    resetAt?: string;
    current?: number;
    overrideActive?: boolean;
    message?: string;
    upgradeMessage?: string;
}

export interface UserAccessLimits {
    subscriptionTier: string;
    gigs: {
        current: number;
        limit: number;
        unlimited: boolean;
        remaining: number;
        resetAt?: string;
        hasActiveOverride: boolean;
    };
    applications: {
        current: number;
        limit: number;
        unlimited: boolean;
        remaining: number;
        resetAt?: string;
        hasActiveOverride: boolean;
    };
    lastUpdated: string;
}

class AccessLimitService {
    private readonly baseUrl = '/access-limits';

    /**
     * Get current user's access limit status
     */
    async getUserAccessLimits(signal?: AbortSignal): Promise<UserAccessLimits> {
        try {
            const response = await api.get(`${this.baseUrl}/user`, {
                signal: signal,
                timeout: 20000 // 20 second timeout for this specific request
            });
            return response.data.data;
        } catch (error: any) {
            // Don't log abort/cancellation errors - they're expected when component unmounts
            if (error?.name !== 'AbortError' &&
                error?.name !== 'CanceledError' &&
                error?.code !== 'ERR_CANCELED' &&
                error?.code !== 'ECONNABORTED') {
                console.error('Error fetching user access limits:', error);
            }
            throw error;
        }
    }

    /**
     * Check if user can create a new gig
     */
    async canCreateGig(): Promise<AccessLimitStatus> {
        try {
            const response = await api.get(`${this.baseUrl}/check/gig`);
            const data = response.data.data;
            const message = this.getGigLimitMessage(data);

            return {
                hasAccess: data.canCreate,
                remaining: data.remaining,
                limit: data.limit,
                isUnlimited: data.isUnlimited,
                requiresUpgrade: data.requiresUpgrade,
                subscriptionTier: data.subscriptionTier,
                resetAt: data.resetAt,
                current: data.current,
                overrideActive: data.overrideActive,
                message,
                upgradeMessage: data.upgradePrompt || message
            };
        } catch (error) {
            console.error('Error checking gig creation limit:', error);
            throw error;
        }
    }

    /**
     * Check if user can apply to a gig
     */
    async canApplyToGig(): Promise<AccessLimitStatus> {
        try {
            const response = await api.get(`${this.baseUrl}/check/application`);
            const data = response.data.data;
            const message = this.getApplicationLimitMessage(data);

            return {
                hasAccess: data.canApply,
                remaining: data.remaining,
                limit: data.limit,
                isUnlimited: data.isUnlimited,
                requiresUpgrade: data.requiresUpgrade,
                subscriptionTier: data.subscriptionTier,
                resetAt: data.resetAt,
                current: data.current,
                overrideActive: data.overrideActive,
                message,
                upgradeMessage: data.upgradePrompt || message
            };
        } catch (error) {
            console.error('Error checking application limit:', error);
            throw error;
        }
    }

    /**
     * Check verification status using the existing verification service
     */
    async getVerificationStatus(): Promise<{
        isVerified: boolean;
        status: VerificationStatus;
        overallStatus: string;
    }> {
        try {
            const verificationData = await verificationService.getVerificationStatus();
            const status = verificationData.data.status;
            return {
                isVerified: status === 'verified',
                status,
                overallStatus: status
            };
        } catch (error) {
            console.error('Error fetching verification status:', error);
            // Default to unverified if service fails
            return {
                isVerified: false,
                status: 'unverified',
                overallStatus: 'unverified'
            };
        }
    }

    /**
     * Handle access limit exceeded errors from API responses
     */
    handleAccessLimitError(error: any): {
        isAccessLimitError: boolean;
        message?: string;
        upgradeMessage?: string;
        limitType?: 'gig' | 'application';
    } {
        if (!error.response?.data) {
            return { isAccessLimitError: false };
        }

        const errorData = error.response.data;
        const errorMessage = errorData.error?.message || errorData.message || '';

        // Check for gig limit errors
        if (errorMessage.includes('gig posting limit') ||
            errorMessage.includes('gig creation limit') ||
            errorMessage.includes('maximum number of gigs')) {
            return {
                isAccessLimitError: true,
                limitType: 'gig',
                message: errorMessage,
                upgradeMessage: 'Verify your account to post unlimited gigs'
            };
        }

        // Check for application limit errors
        if (errorMessage.includes('application limit') ||
            errorMessage.includes('job application limit') ||
            errorMessage.includes('maximum number of applications')) {
            return {
                isAccessLimitError: true,
                limitType: 'application',
                message: errorMessage,
                upgradeMessage: 'Verify your account to apply to unlimited jobs'
            };
        }

        return { isAccessLimitError: false };
    }

    /**
     * Generate friendly message for gig posting limits
     */
    private getGigLimitMessage(data: any): string {
        if (data.isUnlimited) {
            return 'You have unlimited gig posting access';
        }

        // Calculate current usage: limit - remaining
        const current = data.limit - data.remaining;

        if (data.remaining > 0) {
            return `You can post ${data.remaining} more gig${data.remaining === 1 ? '' : 's'} (${current}/${data.limit})`;
        }

        return `You've reached your gig posting limit (${current}/${data.limit})`;
    }

    /**
     * Generate friendly message for application limits
     */
    private getApplicationLimitMessage(data: any): string {
        if (data.isUnlimited) {
            return 'You have unlimited job application access';
        }

        // Calculate current usage: limit - remaining
        const current = data.limit - data.remaining;

        if (data.remaining > 0) {
            return `You can apply to ${data.remaining} more job${data.remaining === 1 ? '' : 's'} (${current}/${data.limit})`;
        }

        return `You've reached your job application limit (${current}/${data.limit})`;
    }

    /**
     * Get upgrade benefits for verification promotion
     */
    getUpgradeBenefits(userRole: 'worker' | 'employer'): string[] {
        const commonBenefits = [
            'Priority support',
            'Verified badge on profile',
            'Enhanced profile visibility',
            'Access to premium features'
        ];

        if (userRole === 'employer') {
            return [
                'Unlimited gig posting',
                ...commonBenefits,
                'Advanced applicant filtering',
                'Bulk application management'
            ];
        } else {
            return [
                'Unlimited job applications',
                ...commonBenefits,
                'Portfolio showcase',
                'Advanced search filters'
            ];
        }
    }

    /**
     * Generate verification CTA message based on user role and context
     */
    getVerificationCTA(_userRole: 'worker' | 'employer', context: 'gig' | 'application'): {
        title: string;
        message: string;
        buttonText: string;
    } {
        if (context === 'gig') {
            return {
                title: 'Unlock Unlimited Gig Posting',
                message: 'Verify your account to post unlimited gigs and access premium employer features.',
                buttonText: 'Start Verification'
            };
        } else {
            return {
                title: 'Unlock Unlimited Applications',
                message: 'Verify your account to apply to unlimited jobs and showcase your verified status to employers.',
                buttonText: 'Start Verification'
            };
        }
    }
}

// Export singleton instance
export const accessLimitService = new AccessLimitService();
export default accessLimitService;